import { Box, Flex, Select, Space, Text } from '@/components';
import { AnalysisGraphBreadcrumbs } from '@/features/analysis/_components/analysis-visualizer/AnalysisGraphBreadcrumbs';
import {
  getCurrentDrillDownOptions,
  getOrganizationNamesWithUpper,
} from '@/features/analysis/_components/analysis-visualizer/AnalysisVisualizer';
import { CustomLegend } from '@/features/analysis/_components/analysis-visualizer/visualizer/CustomLegend';
import { GraphYearSelectorTabs } from '@/features/analysis/_components/analysis-visualizer/visualizer/GraphYearSelectorTabs';
import {
  GraphTypeLabelModifier,
  graphTypeEnum,
} from '@/features/analysis/_models/project';
import { checkChildrenRecursively } from '@/features/analysis/_utils';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useLocaledAmount } from '@/providers';
import { KsAggregationTargetTypeLabelModifier } from '@/utils/enum';
import { isPS } from '@/utils/system';
import Highcharts from 'highcharts';
import {
  HighchartsReact,
  type HighchartsReactRefObject,
} from 'highcharts-react-official';
import TreemapModule from 'highcharts/modules/treemap';
import { type FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import type { KsAnalysisRow } from '../_models/ks-analysis';
import {
  buildGraphTypeChangeUpdate,
  buildYearSelectUpdate,
} from '../_utils/build-table-state-updates';
import {
  getKsFiscalYearLabel,
  getKsGraphOptions,
  getKsLineGraphOptions,
  getKsSeriesData,
} from '../_utils/get-ks-visualizer-options';
import {
  compareByKsSortKeys,
  getKsActiveChartYear,
  resolveKsSortKeys,
} from '../_utils/resolve-ks-ordering';
import { KsFilterHistoryBackButton } from './KsFilterHistoryBackButton';

TreemapModule(Highcharts);

const graphTypeOptions = graphTypeEnum.options.map((value) => ({
  value,
  label: GraphTypeLabelModifier[value],
}));

const noop = () => {
  // enableDrillDown=false の legend では呼び出されない
};

type Props = {
  graphData: KsAnalysisRow[];
};

/**
 * 基金シート分析の可視化エリア。
 *
 * RS 用 `AnalysisVisualizer` と同様のレイアウト（左に凡例 / 右にグラフ）を採用。
 * KS 固有の要件:
 * - その他集計は行わずすべての項目を表示する（チャートクリックでのドリルダウンはしない）
 */
export const KsAnalysisVisualizer: FC<Props> = memo(({ graphData }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { searchQueryStates, setSearchQueryStates } =
    useFundSheetAnalysisPageSearchQueryStates();
  const {
    graphType,
    order,
    chartYear,
    aggregationTargetType,
    fiscalYear,
    analysisAxis,
    cascader,
    organizationIds,
  } = searchQueryStates;
  const headingText =
    KsAggregationTargetTypeLabelModifier[aggregationTargetType];

  // ドリルダウン: KS では「組織軸」のみ階層を持つため、組織軸の時だけドリルダウンする
  const isOrganizationAxis = analysisAxis === 'organization';
  const { data: searchStoreData } = useProjectsSearchStore();
  const allOrganizationsOptions = searchStoreData.organizationsOptions ?? [];
  // 公開サイト(PS)には QueryStore API が無いため、URL に誤って cascader が
  // 付いていても GET /api/query-store/:id を発火させない（404 回避）。
  const queryStoreResult = useQueryStore(cascader, !!cascader && !isPS());
  // 公開サイト(PS)には QueryStore API が存在しないため、ドリルダウン履歴は
  // URL の `organizationIds`(leaf id) を真実とする。内部サイトは従来どおり
  // cascader(QueryStore) から復元する（RS 用 AnalysisVisualizer と同方針）。
  const currentOrganizationLeafIds = isPS()
    ? (organizationIds ?? [])
    : (queryStoreResult.data?.query?.organizationIds?.flatMap(
        (path) => path.at(-1) ?? [],
      ) ?? []);
  const drillDownOptions = getCurrentDrillDownOptions({
    historyIds: currentOrganizationLeafIds,
    options: allOrganizationsOptions,
  });
  const currentDrillDownId = drillDownOptions?.ids.at(-1) ?? null;
  const { mutateAsync: createQueryStore } = useCreateQueryStore();

  const handleGraphInteraction = useCallback(
    async (ids: string[]) => {
      // 公開サイト(PS)は QueryStore を作らず、leaf id を URL に直接保存する。
      // 内部サイトは従来どおり QueryStore を作成し cascader に保存する。
      // `await` 待機中に他の検索条件が変わっても巻き戻さないよう、
      // 更新対象を最小限に絞って prev ベースで上書きする。
      if (isPS()) {
        setSearchQueryStates((prev) => ({
          ...prev,
          organizationIds: ids.length ? [ids.at(-1) ?? ''] : null,
          cascader: null,
        }));
        return;
      }
      const newQueryAndLeafs = generateQueryAndLeafs({
        organizationIds: ids.length ? [ids] : null,
      });
      const nextCascader =
        ids.length && newQueryAndLeafs
          ? ((await createQueryStore(newQueryAndLeafs))?.id ?? null)
          : null;
      setSearchQueryStates((prev) => ({ ...prev, cascader: nextCascader }));
    },
    [createQueryStore, setSearchQueryStates],
  );

  const handlePointClick = useCallback(
    ({ id }: { id: string }) => {
      // ドリルダウン中（単一の現在地あり）は、表示中の子要素を現在地パスに追加する
      if (drillDownOptions?.ids.length) {
        handleGraphInteraction([...drillDownOptions.ids, id]);
        return;
      }
      // 複数選択時（現在地なし）はクリック対象のフルパスを選択済みパスから解決する。
      // [id] のみを渡すと QueryStore に部分パスが保存され検索フォームの復元が壊れるため。
      const fullPath = queryStoreResult.data?.query?.organizationIds?.find(
        (path) => path.at(-1) === id,
      );
      handleGraphInteraction(fullPath ?? [id]);
    },
    [drillDownOptions, handleGraphInteraction, queryStoreResult.data],
  );

  const highchartsRef = useRef<HighchartsReactRefObject | null>(null);
  const [isChartReady, setIsChartReady] = useState(false);

  const fiscalYears = useMemo(
    () => graphData.map((item) => item.fiscalYear),
    [graphData],
  );
  const isPieOrTreeMapGraph =
    graphType === 'pieChart' || graphType === 'treeMap';
  // 円グラフ・ツリーマップの表示年度は chartYear(URL) を真実とし、order(ソート状態)とは独立させる。
  // order をソート解除しても表示年度が既定へ巻き戻らないようにするため (RS-4491)。
  // chartYear 未指定（初期表示）は前年度(n-1)を優先する (RS-4473)。
  const activeYear = getKsActiveChartYear(chartYear, fiscalYears);

  const handleSelectYear = useCallback(
    (year: number) => {
      setSearchQueryStates((prev) => ({
        ...prev,
        ...buildYearSelectUpdate(year, isOrganizationAxis),
      }));
    },
    [setSearchQueryStates, isOrganizationAxis],
  );

  const sortedSeriesData = useMemo(() => {
    const seriesData = getKsSeriesData(graphData);
    // 初期表示は graphType 依存: 積み上げ棒=前年度降順→当年度降順、円/ツリー=選択年度降順 (RS-4487)。
    // 列ヘッダクリック時は order(URL) の単一キーに従う。テーブル(KsAnalysisResultTable)と同期。
    const keys = resolveKsSortKeys(
      order,
      fiscalYears,
      analysisAxis,
      graphType,
      chartYear,
    );
    if (keys.length === 0) {
      return seriesData;
    }
    return [...seriesData].sort(
      compareByKsSortKeys(keys, {
        getTotal: (series, year) =>
          series.data.find((item) => item.x === year)?.y,
        getName: (series) => series.name,
      }),
    );
  }, [graphData, order, fiscalYears, analysisAxis, graphType, chartYear]);

  const filteredSeriesData = useMemo(() => {
    // 円グラフ・ツリーマップでは order 未指定時も activeYear（既定=前年度 n-1）でフィルタする。
    // 全年度でフィルタしないことで凡例（全年度）とグラフ（単一年度）の不一致を防ぐ。
    if (isPieOrTreeMapGraph) {
      return sortedSeriesData
        .map((item) => ({
          ...item,
          data: item.data.filter((d) => d.x === activeYear),
        }))
        .filter((s) => s.data.length);
    }
    return sortedSeriesData;
  }, [isPieOrTreeMapGraph, activeYear, sortedSeriesData]);

  const legendData = useMemo(() => {
    return filteredSeriesData.map(({ name, color, custom }) => {
      const itemId = custom.id;
      // 組織軸の時のみドリルダウン可: 現在 ID と異なり、かつ子組織がツリーに存在する。
      // `checkChildrenRecursively` はルートからのパスで探索するため、
      // 凡例 ID のフルパスを組織ツリーから動的に解決して渡す（非トップレベルでも有効化されるように）
      const fullPath =
        isOrganizationAxis && itemId
          ? getOrganizationNamesWithUpper({
              id: itemId,
              organizations: allOrganizationsOptions,
            }).ids
          : [];
      const enableDrillDown =
        isOrganizationAxis &&
        !!itemId &&
        itemId !== currentDrillDownId &&
        fullPath.length > 0 &&
        checkChildrenRecursively(fullPath, allOrganizationsOptions);
      return {
        name,
        color,
        id: itemId,
        enableDrillDown,
        onClick: enableDrillDown
          ? () => handlePointClick({ id: itemId })
          : noop,
      };
    });
  }, [
    filteredSeriesData,
    isOrganizationAxis,
    currentDrillDownId,
    allOrganizationsOptions,
    handlePointClick,
  ]);

  const chartOptions = useMemo(
    () =>
      getKsLineGraphOptions({
        graphOptions: getKsGraphOptions({
          graphType,
          seriesData: sortedSeriesData,
          selectedYear: activeYear,
          unit,
          onPointClick: isOrganizationAxis ? handlePointClick : null,
          currentId: currentDrillDownId,
        }),
        graphType,
        unit,
        currentFiscalYear: fiscalYear,
      }),
    [
      graphType,
      sortedSeriesData,
      activeYear,
      unit,
      fiscalYear,
      isOrganizationAxis,
      handlePointClick,
      currentDrillDownId,
    ],
  );

  return (
    <Flex vertical gap={16}>
      <Flex style={{ width: '100%' }} justify="space-between" align="flex-end">
        <KsFilterHistoryBackButton disabled={!drillDownOptions?.ids.length} />
        <Space direction="vertical" size="small">
          <Text text={t('graph-type')} />
          <Select
            value={graphType}
            options={graphTypeOptions}
            onChange={(type) => {
              setSearchQueryStates((prev) => ({
                ...prev,
                ...buildGraphTypeChangeUpdate(type, isOrganizationAxis),
              }));
            }}
            style={{ width: 230 }}
          />
        </Space>
      </Flex>

      {isOrganizationAxis && (
        <AnalysisGraphBreadcrumbs
          hideBreadcrumb={currentOrganizationLeafIds.length !== 1}
          options={drillDownOptions}
          handleGraphInteraction={handleGraphInteraction}
          analysisAxis="organization"
        />
      )}

      <Flex gap={8} align="center">
        <CustomLegend
          highchartsRef={highchartsRef}
          legendData={legendData}
          graphType={graphType}
          visibilityStorageKey="ks-analysis-chart-state"
          visibilitySearchQueryStates={
            searchQueryStates as unknown as Record<string, unknown>
          }
        />
        <Box width="80%">
          <Flex justify="center">
            <Text text={headingText} variant="h5" weight="bold" />
          </Flex>
          {isPieOrTreeMapGraph && (
            <Flex align="center" justify="center">
              <GraphYearSelectorTabs
                selectedYear={activeYear}
                fiscalYears={fiscalYears}
                onSelect={handleSelectYear}
                getLabel={(year) => getKsFiscalYearLabel(year, fiscalYear)}
              />
            </Flex>
          )}
          <HighchartsReact
            ref={highchartsRef}
            highcharts={Highcharts}
            callback={() => {
              if (!isChartReady) {
                setIsChartReady(true);
              }
            }}
            options={chartOptions}
          />
        </Box>
      </Flex>
    </Flex>
  );
});
KsAnalysisVisualizer.displayName = 'KsAnalysisVisualizer';
